import { useEffect, useRef, useState } from "react";
import { Plus, Trash2, GripVertical, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import Modal from "@/components/site/Modal";
import YouTubeHoverCard from "@/components/site/YouTubeHoverCard";

/** Extract YouTube ID from URL or raw ID */
function ytId(s) {
  if (!s) return null;
  const t = s.trim();
  if (/^[A-Za-z0-9_-]{11}$/.test(t)) return t;
  const m = t.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/|v\/))([A-Za-z0-9_-]{11})/);
  return m ? m[1] : null;
}

const EMPTY = { title: "", youtube_id: "", artist: "", category: "Music Video" };

export default function VideosAdmin() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/videos").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    const yid = ytId(form.youtube_id);
    if (!yid) { toast.error("Invalid YouTube URL or ID"); return; }
    try {
      await api.post("/admin/videos", { ...form, youtube_id: yid });
      toast.success("Video added");
      setOpen(false); setForm(EMPTY); load();
    } catch { toast.error("Save failed"); }
  };
  const del = async (id, title) => {
    if (!window.confirm(`Delete "${title}"?`)) return;
    try { await api.delete(`/admin/videos/${id}`); load(); }
    catch { toast.error("Delete failed"); }
  };

  /* ---- Drag-to-reorder ---- */
  const dragId = useRef(null);
  const [overId, setOverId] = useState(null);

  const onDragStart = (e, id) => {
    dragId.current = id;
    e.dataTransfer.effectAllowed = "move";
    try { e.dataTransfer.setData("text/plain", id); } catch {}
  };
  const onDragOver = (e, id) => {
    e.preventDefault();
    if (id !== overId) setOverId(id);
  };
  const onDragEnd = () => { dragId.current = null; setOverId(null); };
  const onDrop = async (e, targetId) => {
    e.preventDefault();
    const fromId = dragId.current;
    setOverId(null);
    dragId.current = null;
    if (!fromId || fromId === targetId) return;

    const next = [...rows];
    const fromIdx = next.findIndex((r) => r.id === fromId);
    const toIdx = next.findIndex((r) => r.id === targetId);
    if (fromIdx === -1 || toIdx === -1) return;
    const [moved] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, moved);
    setRows(next); // optimistic

    try {
      await api.post("/admin/videos/reorder", { ids: next.map((r) => r.id) });
    } catch {
      toast.error("Reorder failed — reverting");
      load();
    }
  };

  return (
    <div data-testid="admin-videos">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="label-mono mb-2">/ Catalog</div>
          <h1 className="font-display text-4xl font-bold tracking-tighter">Videos</h1>
          <p className="text-zinc-500 text-sm mt-2">{rows.length} video{rows.length === 1 ? "" : "s"} · drag to reorder</p>
        </div>
        <button onClick={() => setOpen(true)} className="glass-btn solid" data-testid="video-new"><Plus size={14}/> New Video</button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {rows.map((v) => (
          <div
            key={v.id}
            draggable
            onDragStart={(e) => onDragStart(e, v.id)}
            onDragOver={(e) => onDragOver(e, v.id)}
            onDrop={(e) => onDrop(e, v.id)}
            onDragEnd={onDragEnd}
            className={`relative space-y-3 p-2 rounded-2xl border transition-all group ${overId === v.id ? "border-cyan-400/60 ring-2 ring-cyan-400/30 bg-cyan-400/5" : "border-transparent"}`}
            data-testid={`video-row-${v.id}`}
          >
            <div
              className="absolute top-3 left-3 z-20 w-8 h-8 rounded-full bg-black/70 backdrop-blur border border-white/10 flex items-center justify-center text-zinc-400 cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 transition-opacity"
              title="Drag to reorder"
              aria-label="Drag handle"
              data-testid={`video-drag-${v.id}`}
            >
              <GripVertical size={14} />
            </div>
            <div className="pointer-events-none [&>*]:pointer-events-auto">
              <YouTubeHoverCard youtubeId={v.youtube_id} title={v.title} category={v.category} />
            </div>
            <div className="flex items-center justify-between px-1">
              <div className="text-xs text-zinc-500 truncate">{v.artist}</div>
              <div className="flex gap-2">
                <a
                  href={`https://youtu.be/${v.youtube_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="glass-btn ghost p-2"
                  aria-label="Open on YouTube"
                  data-testid={`video-open-${v.id}`}
                >
                  <ExternalLink size={14}/>
                </a>
                <button onClick={() => del(v.id, v.title)} className="glass-btn ghost p-2" data-testid={`video-del-${v.id}`}>
                  <Trash2 size={14}/>
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title="New Video" testid="video-modal">
        <form onSubmit={save} className="space-y-3" data-testid="video-form">
          <div>
            <label className="label-mono block mb-2">Title *</label>
            <input required className="pulse-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="vid-title"/>
          </div>
          <div>
            <label className="label-mono block mb-2">YouTube URL or video ID *</label>
            <input required className="pulse-input" placeholder="https://youtu.be/dQw4w9WgXcQ" value={form.youtube_id} onChange={(e) => setForm({ ...form, youtube_id: e.target.value })} data-testid="vid-yid"/>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label-mono block mb-2">Artist</label>
              <input className="pulse-input" value={form.artist} onChange={(e) => setForm({ ...form, artist: e.target.value })}/>
            </div>
            <div>
              <label className="label-mono block mb-2">Category</label>
              <select className="pulse-input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                <option>Music Video</option>
                <option>Live Session</option>
                <option>Visualizer</option>
                <option>Trailer</option>
                <option>BTS</option>
                <option>Brand</option>
              </select>
            </div>
          </div>
          <button className="glass-btn solid w-full justify-center" data-testid="vid-save">Save</button>
        </form>
      </Modal>
    </div>
  );
}
